import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Award, LogOut, Settings, ShieldCheck, Sparkles } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";
import ImageUpload from "@/components/ImageUpload";
import CollectibleCard from "@/components/CollectibleCard";

function Card({ title, children, testId }) {
  return (
    <section className="mt-7" data-testid={testId}>
      <p className="eyebrow">{title}</p>
      <div className="mt-3 space-y-2">{children}</div>
    </section>
  );
}

const Empty = ({ children }) => (
  <p className="rounded-2xl border bg-white px-4 py-4 text-sm text-[var(--ink-soft)]">{children}</p>
);

function walkProgressLabel(item) {
  if (item.status === "COMPLETED") return "Abgeschlossen";
  if (item.walk_experience_mode === "OPEN_COLLECTION") {
    const count = new Set(item.discovered_artworks || []).size;
    return `${count} Kunstwerk${count === 1 ? "" : "e"} entdeckt`;
  }
  const count = (item.completed_stations || []).length;
  return `${count} Station${count === 1 ? "" : "en"} erledigt`;
}

export default function Profile() {
  const { user, loading, logout, isAdmin, setUser } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [confirmations, setConfirmations] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ display_name: "", avatar: null });

  const load = useCallback(() => {
    if (!user) return;
    api.get("/me/overview").then(({ data }) => setOverview(data)).catch(() => setOverview(null));
    api.get("/me/confirmations").then(({ data }) => setConfirmations(data)).catch(() => setConfirmations(null));
  }, [user]);

  useEffect(load, [load]);
  useEffect(() => {
    if (user) setProfileForm({ display_name: user.display_name || "", avatar: user.avatar || null });
  }, [user]);

  if (loading) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;
  if (!user)
    return (
      <div className="mx-auto max-w-md px-5 py-16 text-center" data-testid="profile-anonymous">
        <h1 className="text-4xl">Profil</h1>
        <p className="mt-3 text-sm text-[var(--ink-soft)]">
          Melde dich an für gespeicherten Fortschritt, SPUREN-Karten, XP, Challenges und Erfolge.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Link to="/login" className="btn btn-primary" data-testid="profile-login">ANMELDEN</Link>
          <Link to="/registrieren" className="btn btn-ghost">REGISTRIEREN</Link>
        </div>
      </div>
    );

  const act = async (path, body) => {
    setError("");
    try {
      await api.post(path, body);
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const saveProfile = async () => {
    setError("");
    try {
      const { data } = await api.put("/me/profile", profileForm);
      setUser({ ...user, display_name: data.display_name, avatar: data.avatar });
      setEditing(false);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const c = confirmations || {};

  return (
    <div data-testid="profile-page">
      <TopBar title="Profil" />
      <div className="mx-auto max-w-2xl px-5 py-8">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 overflow-hidden rounded-2xl bg-[var(--brand-soft)]">
            {user.avatar ? <img src={fileUrl(user.avatar)} alt="" className="h-full w-full object-cover" /> : null}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-3xl" data-testid="profile-name">{user.display_name}</h1>
            <p className="truncate text-xs text-[var(--ink-soft)]">{user.email}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button className="btn btn-ghost !py-2 !text-xs" onClick={() => setEditing(!editing)} data-testid="profile-settings">
            <Settings className="h-4 w-4" /> EINSTELLUNGEN
          </button>
          {isAdmin ? (
            <button className="btn btn-ghost !py-2 !text-xs" onClick={() => navigate("/admin")} data-testid="profile-admin">
              <ShieldCheck className="h-4 w-4" /> ADMINBEREICH
            </button>
          ) : null}
          <button className="btn btn-ghost !py-2 !text-xs" onClick={async () => { await logout(); navigate("/"); }} data-testid="profile-logout">
            <LogOut className="h-4 w-4" /> ABMELDEN
          </button>
        </div>

        {editing ? (
          <div className="mt-5 space-y-4 rounded-2xl border bg-white p-5" data-testid="profile-form">
            <div>
              <div className="eyebrow mb-1">Anzeigename</div>
              <input className="field" value={profileForm.display_name} onChange={(e) => setProfileForm({ ...profileForm, display_name: e.target.value })} data-testid="profile-display-name" />
            </div>
            <ImageUpload value={profileForm.avatar} onChange={(v) => setProfileForm({ ...profileForm, avatar: v })} label="Profilbild" testId="profile-avatar" />
            <button className="btn btn-primary" onClick={saveProfile} data-testid="profile-save">SPEICHERN</button>
          </div>
        ) : null}

        {error ? <p className="mt-4 text-sm text-red-700" data-testid="profile-error">{error}</p> : null}

        <section className="mt-7 overflow-hidden rounded-3xl bg-[var(--brand)] p-5 text-white" data-testid="profile-xp">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">SPUREN-XP</p>
              <p className="mt-1 font-serif text-4xl" data-testid="profile-xp-balance">{overview?.xp_balance || 0}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
              <Sparkles className="h-6 w-6" />
            </span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-white/75">Für abgeschlossene Wege. Jede Belohnung wird nur einmal vergeben.</p>
        </section>

        <Card title={`Meine SPUREN-Karten${(overview?.collectibles || []).length ? ` (${overview.collectibles.length})` : ""}`} testId="profile-collectibles">
          {(overview?.collectibles || []).length === 0 ? <Empty>Noch keine Karte gesammelt. Schließe deine erste SPUR ab.</Empty> : null}
          {(overview?.collectibles || []).length ? (
            <div className="grid gap-6 sm:grid-cols-2">
              {overview.collectibles.map((item) => (
                <CollectibleCard key={item.id} value={item} compact testId={`profile-collectible-${item.walk_id}`} />
              ))}
            </div>
          ) : null}
        </Card>

        <Card title={`Offene Bestätigungen${c.total ? ` (${c.total})` : ""}`} testId="profile-confirmations">
          {!c.total ? <Empty>Nichts offen.</Empty> : null}
          {(c.challenge_entries || []).map((entry) => (
            <div key={entry.id} className="rounded-2xl border bg-white p-4" data-testid={`confirm-challenge-${entry.id}`}>
              <p className="text-sm">
                <strong>{entry.user_name}</strong> gibt an, {entry.result_value} {entry.unit_label || ""} geschafft zu haben.
              </p>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">{entry.challenge_title}</p>
              <div className="mt-3 flex gap-2">
                <button className="btn btn-primary !py-2 !text-xs" onClick={() => act(`/challenge-entries/${entry.id}/confirm`, { approve: true })} data-testid={`confirm-challenge-yes-${entry.id}`}>BESTÄTIGEN</button>
                <button className="btn btn-ghost !py-2 !text-xs" onClick={() => act(`/challenge-entries/${entry.id}/confirm`, { approve: false })} data-testid={`confirm-challenge-no-${entry.id}`}>ABLEHNEN</button>
              </div>
            </div>
          ))}
          {(c.matches || []).map((match) => (
            <div key={match.id} className="rounded-2xl border bg-white p-4" data-testid={`confirm-match-${match.id}`}>
              <p className="text-sm">
                <strong>{match.player_a_name}</strong> hat folgendes Ergebnis eingetragen:
              </p>
              <p className="mt-1 text-lg">
                {(match.sets || []).length ? match.sets.map((s) => `${s.a}:${s.b}`).join("   ") : `${match.score_a} : ${match.score_b}`}
              </p>
              <p className="text-xs text-[var(--ink-soft)]">{match.game_title}</p>
              <div className="mt-3 flex gap-2">
                <button className="btn btn-primary !py-2 !text-xs" onClick={() => act(`/matches/${match.id}/confirm`, { approve: true })} data-testid={`confirm-match-yes-${match.id}`}>BESTÄTIGEN</button>
                <button className="btn btn-ghost !py-2 !text-xs" onClick={() => act(`/matches/${match.id}/confirm`, { approve: false })} data-testid={`confirm-match-no-${match.id}`}>STIMMT NICHT</button>
              </div>
            </div>
          ))}
          {(c.loser_invites || []).map((invite) => (
            <div key={invite.id} className="rounded-2xl border bg-white p-4" data-testid={`confirm-loser-${invite.id}`}>
              <p className="text-sm"><strong>{invite.player_a_name}</strong> fordert dich zu {invite.word} heraus.</p>
              <Link to={`/loser/${invite.id}`} className="btn btn-primary mt-3 !py-2 !text-xs" data-testid={`open-loser-${invite.id}`}>ÖFFNEN</Link>
            </div>
          ))}
          {(c.loser_trick_confirmations || []).map((match) => (
            <div key={match.id} className="rounded-2xl border bg-white p-4" data-testid={`confirm-trick-${match.id}`}>
              <p className="text-sm">Ein Trick-Versuch wartet auf deine Bestätigung.</p>
              <Link to={`/loser/${match.id}`} className="btn btn-primary mt-3 !py-2 !text-xs">ÖFFNEN</Link>
            </div>
          ))}
          {(c.love_messages || []).map((message) => (
            <div key={message.id} className="rounded-2xl border bg-white p-4" data-testid={`confirm-love-${message.id}`}>
              <p className="text-sm"><strong>{message.author_name}</strong> möchte gemeinsam folgende Botschaft veröffentlichen:</p>
              <p className="mt-2 whitespace-pre-line rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm">{message.message}</p>
              <div className="mt-3 flex gap-2">
                <button className="btn btn-primary !py-2 !text-xs" onClick={() => act(`/love-tree/messages/${message.id}/respond`, { approve: true })} data-testid={`confirm-love-yes-${message.id}`}>BESTÄTIGEN</button>
                <button className="btn btn-ghost !py-2 !text-xs" onClick={() => act(`/love-tree/messages/${message.id}/respond`, { approve: false })} data-testid={`confirm-love-no-${message.id}`}>ABLEHNEN</button>
              </div>
            </div>
          ))}
        </Card>

        <Card title="Meine Wege" testId="profile-walks">
          {(overview?.walks || []).length === 0 ? <Empty>Noch keinen Weg gestartet.</Empty> : null}
          {(overview?.walks || []).map((item) => (
            <Link key={item.id} to={`/wege/${item.walk_id}`} className="flex items-center gap-3 rounded-2xl border bg-white p-4">
              <div className="h-12 w-16 shrink-0 overflow-hidden rounded-lg bg-[var(--brand-soft)]">
                {item.walk_hero_image ? <img src={fileUrl(item.walk_hero_image)} alt="" className="h-full w-full object-cover" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{item.walk_title || "Weg"}</p>
                <p className="text-xs text-[var(--ink-soft)]">{walkProgressLabel(item)}</p>
              </div>
            </Link>
          ))}
        </Card>

        <Card title="Meine Challenges" testId="profile-challenges">
          {(overview?.challenge_entries || []).length === 0 ? <Empty>Noch keine Challenge gemacht.</Empty> : null}
          {(overview?.challenge_entries || []).map((entry) => (
            <div key={entry.id} className="rounded-2xl border bg-white p-4 text-sm">
              <div className="flex justify-between gap-3">
                <span className="truncate">{entry.challenge_title}</span>
                <span className="font-semibold">{entry.result_value} {entry.unit_label || ""}</span>
              </div>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                {entry.status === "CONFIRMED" ? "bestätigt" : entry.status === "PENDING" ? "wartet auf Bestätigung" : "abgelehnt"}
              </p>
            </div>
          ))}
        </Card>

        <Card title="Meine Matches" testId="profile-matches">
          {(overview?.matches || []).length === 0 && (overview?.loser_matches || []).length === 0 ? <Empty>Noch keine Matches.</Empty> : null}
          {(overview?.matches || []).map((match) => (
            <div key={match.id} className="rounded-2xl border bg-white p-4 text-sm">
              <div className="flex justify-between gap-3">
                <span className="truncate">{match.player_a_name} vs {match.player_b_name}</span>
                <span className="font-semibold">{match.score_a} : {match.score_b}</span>
              </div>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                {match.game_title} · {match.status === "CONFIRMED" ? "bestätigt" : match.status === "PENDING" ? "wartet auf Bestätigung" : "widersprochen"}
              </p>
            </div>
          ))}
          {(overview?.loser_matches || []).map((match) => (
            <Link key={match.id} to={`/loser/${match.id}`} className="block rounded-2xl border bg-white p-4 text-sm">
              <div className="flex justify-between gap-3">
                <span className="truncate">{match.player_a_name} vs {match.player_b_name}</span>
                <span className="font-semibold">{match.a_letters} : {match.b_letters}</span>
              </div>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">{match.word} · {match.status.toLowerCase()}</p>
            </Link>
          ))}
        </Card>

        <Card title="Erfolge" testId="profile-achievements">
          {(overview?.achievements || []).length === 0 ? <Empty>Noch keine Erfolge.</Empty> : null}
          {(overview?.achievements || []).map((achievement) => (
            <div key={achievement.id} className="flex items-center gap-3 rounded-2xl border bg-white p-4">
              <Award className="h-5 w-5 shrink-0" style={{ color: "var(--brand-accent)" }} />
              <div>
                <p className="text-sm font-semibold">{achievement.title}</p>
                {achievement.description ? <p className="text-xs text-[var(--ink-soft)]">{achievement.description}</p> : null}
              </div>
            </div>
          ))}
        </Card>

        <Card title="Meine Liebesbotschaften" testId="profile-love">
          {(overview?.love_messages || []).length === 0 ? <Empty>Noch keine Botschaft.</Empty> : null}
          {(overview?.love_messages || []).map((message) => (
            <div key={message.id} className="rounded-2xl border bg-white p-4 text-sm">
              <p className="font-semibold">{message.author_name} & {message.partner_name}</p>
              <p className="mt-1 whitespace-pre-line">{message.message}</p>
              <p className="mt-2 text-xs text-[var(--ink-soft)]">
                {message.status === "PUBLIC" ? "öffentlich" : message.status === "PENDING" ? "wartet auf Bestätigung" : "abgelehnt"}
              </p>
              {message.status === "PENDING" && message.author_id === user.id ? (
                <button
                  className="btn btn-ghost mt-2 !py-2 !text-xs"
                  onClick={async () => {
                    try {
                      await api.delete(`/love-tree/messages/${message.id}`);
                      load();
                    } catch (e) {
                      setError(errMsg(e));
                    }
                  }}
                  data-testid={`withdraw-love-${message.id}`}
                >
                  ZURÜCKZIEHEN
                </button>
              ) : null}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
